import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataMap, CoordinatingGroup, Institution, MasterGroup, Metadata, SourceDataFinding, SourceDataFindingStatus, SourceDataFindingType, Participant, SourceData, Trial } from "@/api/dtos";
import SearchDialog, { ISearchField, ISearchForm } from "@/components/dialogs/SearchDialog";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import ParticipantsContext from "@/contexts/data/ParticipantsContext";
import TrialsContext from "@/contexts/data/TrialsContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, Button, ButtonGroup, List, Paper, Theme, Tooltip, Typography, styled, useTheme } from "@mui/material";
import { immerable } from "immer";
import { FC, memo, useCallback, useContext, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import SourceDataFindingDetails from "./SourceDataFindingDetails";
import SourceDataFindingView from "./SourceDataFindingView";
import SourceDataFindingsListSort, { DefaultSortableFields, ISourceDataFindingsListSortable, SourceDataFindingsListFilter } from "./SourceDataFindingsListSort";
import MetadataContext from "@/contexts/data/MetadataContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import sourceDataFindingTypeToText from "@/utilities/configuration/sourceDataFindingTypeToText";
import SourceDataFindingAddForm from "@/components/forms/forms/findings/SourceDataFindingForm";
import { DateTime } from "luxon";


const Subtitle = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2)
}));



interface IDetailsProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;
}

const Details: FC<IDetailsProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName
}) => {
    const theme = useTheme();
    const { idMap: trials } = useContext(TrialsContext);
    const { idMap: masterGroups } = useContext(MasterGroupsContext);
    const { idMap: coordinatingGroups } = useContext(CoordinatingGroupsContext);
    const { idMap: institutions } = useContext(InstitutionsContext);
    const { idMap: participants } = useContext(ParticipantsContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: configuration } = useContext(ConfigurationSourceDataContext);
    const { data: sourceData } = useContext(SourceDataContext);

    return (
        <>
            <Subtitle variant="h2">
                Details
            </Subtitle>
            <Paper>
                <SourceDataFindingDetails 
                    trialId={trialId}
                    masterGroupId={masterGroupId}
                    coordinatingGroupId={coordinatingGroupId}
                    institutionId={institutionId}
                    participantId={participantId}
                    studyEventOid={studyEventOid}
                    studyEventRepeat={studyEventRepeat}
                    caseReportFormOid={caseReportFormOid}
                    caseReportFormVersionOid={caseReportFormVersionOid}
                    itemGroupName={itemGroupName}
                    itemGroupRepeat={itemGroupRepeat}
                    itemName={itemName}

                    trials={trials}
                    masterGroups={masterGroups}
                    coordinatingGroups={coordinatingGroups}
                    institutions={institutions}
                    participants={participants}
                    metadata={metadata}
                    trialConfiguration={trialConfiguration}
                    configuration={configuration}
                    sourceData={sourceData}

                    enableLinks={false}
                />
            </Paper>
        </>
    )
}

interface ISourceDataFindingsAddProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;
    hideDetails?: boolean;
}

const SourceDataFindingsAdd: FC<ISourceDataFindingsAddProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName,

    hideDetails
}) => {

    
    const initialData = useMemo(() => {
        return new SourceDataFinding({
            trialId,
            masterGroupId,
            coordinatingGroupId,
            institutionId,
            participantId,
            studyEventOid,
            studyEventRepeat,
            caseReportFormOid,
            caseReportFormVersionOid,
            itemGroupName,
            itemGroupRepeat,
            itemName,
            type: SourceDataFindingType.Information,
            createdDate: DateTime.now().toISO({ includeOffset: false }) ?? undefined
        })

    }, [
        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        itemGroupRepeat,
        itemName
    ]);

    return (
        <>
            {
                !hideDetails && (
                    <Details
                        trialId={trialId}
                        masterGroupId={masterGroupId}
                        coordinatingGroupId={coordinatingGroupId}
                        institutionId={institutionId}
                        participantId={participantId}
                        studyEventOid={studyEventOid}
                        studyEventRepeat={studyEventRepeat}
                        caseReportFormOid={caseReportFormOid}
                        caseReportFormVersionOid={caseReportFormVersionOid}
                        itemGroupName={itemGroupName}
                        itemGroupRepeat={itemGroupRepeat}
                        itemName={itemName}
                    />
                )
            }
            <Subtitle variant="h2">
                Add Finding
            </Subtitle>
            <SourceDataFindingAddForm 
                sourceDataFinding={initialData}
            />
        </>
    )
}

export default SourceDataFindingsAdd;